import { parseLocaleFromPath } from "@/i18n/LocaleContext";

/**
 * Validates a post-auth redirect target so it can only navigate within
 * the same origin.  Accepts relative paths that start with "/" and
 * rejects anything else — absolute URLs, protocol-relative URLs (//…),
 * and javascript: payloads.
 *
 * Returns the sanitised path, or "/" when the value is absent / unsafe.
 */
export function safeRedirectPath(next: string | null | undefined): string {
  if (!next) return "/";
  const trimmed = next.trim();
  if (trimmed.startsWith("/") && !trimmed.startsWith("//") && !/[\\\u0000-\u001f\u007f]/.test(trimmed)) {
    return trimmed;
  }
  return "/";
}

/** Capture the full app-relative browser URL, including query and anchor. */
export function browserAuthReturnPath(
  location: Pick<Location, "pathname" | "search" | "hash">,
  base: string,
): string {
  const prefix = base.replace(/\/$/, "");
  let pathname = location.pathname || "/";
  if (prefix && (pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    pathname = pathname.slice(prefix.length) || "/";
  }
  return safeRedirectPath(`${pathname}${location.search || ""}${location.hash || ""}`);
}

/**
 * Explicit destinations win. If a caller passed only the current router path,
 * restore the browser's market, query and anchor rather than losing page state.
 * Calls without a destination resume the current page, except auth entry pages.
 */
export function resolveAuthReturnPath(next: string | null | undefined, current: string): string {
  const safeCurrent = safeRedirectPath(current);
  const currentParts = /^([^?#]*)(\?[^#]*)?(#.*)?$/.exec(safeCurrent)!;
  const innerPath = parseLocaleFromPath(currentParts[1]).innerPath;
  if (next == null || next === "") {
    return ["/login", "/signup", "/auth", "/auth/callback", "/forgot-password", "/reset-password"]
      .includes(innerPath) ? "/" : safeCurrent;
  }
  const safeNext = safeRedirectPath(next);
  if (safeNext === "/") return "/";
  const nextParts = /^([^?#]*)(\?[^#]*)?(#.*)?$/.exec(safeNext)!;
  if (nextParts[1] !== currentParts[1] && nextParts[1] !== innerPath) return safeNext;
  const query = nextParts[2] ?? currentParts[2] ?? "";
  const anchor = nextParts[3] ??
    (nextParts[2] == null || nextParts[2] === currentParts[2] ? currentParts[3] : "") ?? "";
  return `${currentParts[1]}${query}${anchor}`;
}

/** Already-localized return URLs must bypass the locale router on every route. */
export function postAuthWouterDestination(path: string): string {
  const { market } = parseLocaleFromPath(path.split(/[?#]/, 1)[0]);
  return market ? `~${path}` : path;
}
